// The quote beside the review, one pane per part (#2018, C10).
import React, { useEffect, useState } from "react";
import { parseCsvRows, type QuoteIntakeView } from "@phoneware/lens-domain";
import { fileSize, partChip, sourceLabel } from "./api-intakes.js";
import { Icon } from "./icons.js";
import { PdfPane, type PdfHighlight } from "./pdf-view.js";
import { useSession } from "./session.js";

export function SourcePane({
  intake,
  mode,
  part,
  onPart,
  highlight,
  mark,
  open,
  onToggle,
  onPages,
  staff,
}: {
  intake: QuoteIntakeView;
  mode: "reading" | "review" | "fail" | "done";
  part: number;
  onPart: (index: number) => void;
  highlight: PdfHighlight | null;
  mark: string | null;
  open: boolean;
  onToggle: () => void;
  onPages?: (n: number) => void;
  staff: boolean;
}): React.ReactElement {
  const parts = intake.parts ?? [];
  const current = parts.find((p) => p.index === part) ?? parts[0] ?? null;
  const kqm = intake.source.kind === "kqm" ? intake.source : null;
  return (
    <aside className={`ix-src${open ? " open" : ""}${mode === "reading" ? " reading" : ""}`} aria-label="The quote" data-testid="source-pane">
      {kqm ? <KqmCard source={kqm} staff={staff} waiting={mode === "reading"} /> : null}
      {parts.length > 1 ? (
        <div className="ptabs" role="tablist" aria-label="Parts of the quote">
          {parts.map((p) => (
            <button key={p.index} type="button" role="tab" className="ptab" aria-selected={current?.index === p.index} data-testid={`part-${p.index}`} onClick={() => onPart(p.index)}>
              {p.label}
            </button>
          ))}
        </div>
      ) : null}
      {current?.kind === "pdf" ? (
        <PdfPane intakeId={intake.id} partIndex={current.index} filename={current.label} bytes={current.bytes} mode={mode} highlight={highlight} open={open} onToggle={onToggle} onPages={onPages} />
      ) : (
        <div className="ix-file">
          <div className="pbar">
            <Icon name="file-text" size={14} />
            <b title={sourceLabel(intake.source)}>{current?.label ?? sourceLabel(intake.source)}</b>
            <span>{current ? fileSize(current.bytes) : ""}</span>
            <button className="btn small ptoggle" type="button" aria-expanded={open} data-testid="source-toggle" onClick={onToggle}>
              <Icon name={open ? "x" : "eye"} size={14} />
              {open ? "Hide the quote" : "Show the quote"}
            </button>
          </div>
          {(open || mode !== "reading") && (!kqm || current) ? (
            <div className="pscroll">
              {current?.kind === "image" ? <ImagePart intakeId={intake.id} index={current.index} label={current.label} /> : null}
              {current?.kind === "table" ? <TablePart intakeId={intake.id} index={current.index} mark={mark} /> : null}
              {current?.kind === "text" ? <TextPart intakeId={intake.id} index={current.index} mark={mark} /> : null}
              {!current && !kqm ? <p className="empty">Omni is still preparing the quote.</p> : null}
            </div>
          ) : null}
        </div>
      )}
    </aside>
  );
}

function KqmCard({ source, staff, waiting }: { source: Extract<QuoteIntakeView["source"], { kind: "kqm" }>; staff: boolean; waiting: boolean }): React.ReactElement {
  return (
    <div className={waiting ? "kqmcard wait" : "kqmcard"} data-testid="kqm-card">
      <div className="kq-top">
        <span className="kq-mark" aria-hidden="true">KQM</span>
        <div className="kq-who">
          <b>Quote # {source.quoteNumber}</b>
          <span>{source.title ?? "KQM quote"}</span>
        </div>
        {staff ? (
          <a className="btn small white" href={source.url} target="_blank" rel="noopener noreferrer" data-testid="open-kqm">
            <Icon name="external-link" size={13} />
            Open in KQM
          </a>
        ) : null}
      </div>
      <p className="kq-note">
        {staff ? "Read from KQM. Omni never changes anything in KQM." : "Phoneware brought this quote in from its KQM."}
      </p>
    </div>
  );
}

function usePartText(intakeId: string, index: number): string | null {
  const { api } = useSession();
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    api.quoteIntakePart(intakeId, index).then(async (blob) => {
      const value = await blob.text();
      if (live) setText(value);
    }).catch(() => {
      if (live) setText("");
    });
    return () => {
      live = false;
    };
  }, [api, intakeId, index]);
  return text;
}

function ImagePart({ intakeId, index, label }: { intakeId: string; index: number; label: string }): React.ReactElement {
  const { api } = useSession();
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    let url: string | null = null;
    api.quoteIntakePart(intakeId, index).then((blob) => {
      if (!live || typeof URL.createObjectURL !== "function") return;
      url = URL.createObjectURL(blob);
      setSrc(url);
    }).catch(() => undefined);
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [api, intakeId, index]);
  return src ? <div className="imgpart"><img src={src} alt={label} /></div> : <p className="empty">Loading the image.</p>;
}

function TablePart({ intakeId, index, mark }: { intakeId: string; index: number; mark: string | null }): React.ReactElement {
  const text = usePartText(intakeId, index);
  if (text == null) return <p className="empty">Loading the sheet.</p>;
  const rows = parseCsvRows(text);
  const needle = (mark ?? "").trim().toLowerCase();
  return (
    <div className="sgridwrap">
      <table className="sgrid">
        <tbody>
          {rows.map((row, i) => {
            const hit = needle.length > 0 && row.some((cell) => cell.toLowerCase().includes(needle));
            return (
              <tr key={i} className={hit ? "hl" : undefined}>
                {row.map((cell, k) => <td key={k}>{cell}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TextPart({ intakeId, index, mark }: { intakeId: string; index: number; mark: string | null }): React.ReactElement {
  const text = usePartText(intakeId, index);
  if (text == null) return <p className="empty">Loading the text.</p>;
  const needle = (mark ?? "").trim();
  const at = needle ? text.toLowerCase().indexOf(needle.toLowerCase()) : -1;
  if (at < 0) return <pre className="srctext">{text}</pre>;
  return (
    <pre className="srctext">
      {text.slice(0, at)}
      <mark>{text.slice(at, at + needle.length)}</mark>
      {text.slice(at + needle.length)}
    </pre>
  );
}

export { partChip };
